import { useEffect, useRef } from 'react'
import {
  HubConnectionBuilder,
  HubConnection,
  LogLevel,
  HubConnectionState,
} from '@microsoft/signalr'

const SIGNALR_URL = process.env.NEXT_PUBLIC_CRS_URL

interface SignalRHookOptions {
  onMessage?: (data: any) => void
  onError?: (error: any) => void
  onClose?: () => void
  onOpen?: () => void
}

function registerOnAny(conn: HubConnection, handler: (method: string, args: any[]) => void) {
  const originalOn = conn.on.bind(conn)

  conn.on = (methodName: string, newMethod: (...args: any[]) => void) => {
    originalOn(methodName, (...args: any[]) => {
      handler(methodName, args) // gọi global handler
      newMethod(...args) // gọi handler gốc
    })
  }
}

export function useSignalR(userId: string, options: SignalRHookOptions = {}) {
  const connectionRef = useRef<HubConnection | null>(null)

  const { onMessage, onError, onClose, onOpen } = options

  useEffect(() => {
    if (!userId) return

    const fullUrl = `${SIGNALR_URL}/bev2/bookingHub?userId=${encodeURIComponent(userId)}`

    const connection = new HubConnectionBuilder()
      .withUrl(fullUrl, {
        skipNegotiation: false,
        transport: 4,
        withCredentials: false,
      })
      .configureLogging(LogLevel.Information)
      .withAutomaticReconnect()
      .build()

    connectionRef.current = connection

    connection.on('ReceiveMessage', (data) => {
      onMessage?.(data)
    })

    connection.onclose((error) => {
      onClose?.()
      if (error) {
        onError?.(error)
      }
    })

    connection
      .start()
      .then(() => {
        onOpen?.()
      })
      .catch((err) => {
        onError?.(err)
      })

    return () => {
      connection.stop()
    }
  }, [userId])

  // Gửi message nếu backend có method cho phép
  const sendMessage = (method: string, message: any) => {
    if (connectionRef.current && connectionRef.current.state === HubConnectionState.Connected) {
      connectionRef.current.invoke(method, message)
    } else {
      console.warn('⚠️ SignalR is not connected')
    }
  }

  return {
    sendMessage,
    connection: connectionRef.current,
  }
}
